import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErroreApi, chiama } from '../api/cliente.js';
import type { Ordine } from '../api/ordini.js';
import { Modale } from '../componenti/Modale.js';
import { t } from '../i18n/lingua.js';

/**
 * Correggere lo stato di un ordine a mano.
 *
 * Serve quando il cliente chiede di sistemare dei passaggi fatti male: finora
 * l'unico modo era aprire il database, mezz'ora di lavoro e nessuna traccia
 * di chi avesse cambiato cosa.
 *
 * Non è l'avanzamento normale e non deve sembrarlo: sta dietro a una voce
 * separata, chiede un motivo scritto, e lo mette nello storico. Fra due anni
 * il motivo è l'unica cosa che spiega perché quell'ordine è tornato indietro.
 */
export function ModaleCorrezioneStato({
  ordine,
  onChiudi,
}: {
  ordine: Ordine;
  onChiudi: () => void;
}) {
  const client = useQueryClient();
  const [statoId, setStatoId] = useState('');
  const [motivo, setMotivo] = useState('');

  const stati = useQuery({
    queryKey: ['stati-ordine'],
    queryFn: () =>
      chiama<{ states: { id: number; sortOrder: number; name: string }[] }>('/api/orders/states'),
    staleTime: 10 * 60_000,
  });

  const correggi = useMutation({
    mutationFn: (corpo: Record<string, unknown>) =>
      chiama<Ordine>(`/api/orders/${ordine.id}/set-state`, { metodo: 'POST', corpo }),
    onSuccess: (aggiornato) => {
      client.setQueryData(['ordine', ordine.id], aggiornato);
      void client.invalidateQueries({ queryKey: ['ordini'] });
      void client.invalidateQueries({ queryKey: ['ordini-conteggi'] });
      void client.invalidateQueries({ queryKey: ['transizioni', ordine.id] });
      onChiudi();
    },
  });

  const errore = correggi.error instanceof ErroreApi ? correggi.error.message : null;
  const scelti = (stati.data?.states ?? []).filter((s) => s.id !== ordine.stateId);

  return (
    <Modale
      titolo={t('Correggi lo stato')}
      occhiello={t('Adesso è: {stato}', { stato: ordine.state })}
      nota={t('Serve a rimettere a posto passaggi fatti male. Non segue le regole di avanzamento — le scavalca — quindi resta scritto nello storico con il motivo e con chi l’ha fatto.')}
      larga
      inCorso={correggi.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={correggi.isPending}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-pericolo"
            onClick={() =>
              correggi.mutate({ toStateId: Number(statoId), reason: motivo.trim() })
            }
            disabled={correggi.isPending || statoId === '' || motivo.trim().length < 3}
          >
            {correggi.isPending ? 'Correggo…' : t('Correggi lo stato')}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      <div className="fields">
        <div className="field span-2">
          <label htmlFor="cs-stato">
            {t('Portalo allo stato')}<span className="req">*</span>
          </label>
          <select
            id="cs-stato"
            className="select"
            value={statoId}
            onChange={(e) => setStatoId(e.target.value)}
          >
            <option value="">{t('Scegli…')}</option>
            {scelti.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.sortOrder < ordine.stateOrder ? ' — indietro' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="field span-2">
          <label htmlFor="cs-motivo">
            {t('Perché lo stai correggendo')}<span className="req">*</span>
          </label>
          <textarea
            id="cs-motivo"
            className="textarea"
            rows={3}
            placeholder={t('Es. il cliente ha chiesto di rifare i passaggi: la merce non era pronta')}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
          <span className="dim" style={{ fontSize: 11 }}>
            {t('Resta nello storico dell’ordine, insieme al tuo nome.')}
          </span>
        </div>
      </div>
    </Modale>
  );
}
